import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';

export interface CapturedHttpError {
	message: string | null;
	status: number;
	method: string;
	url: string;
	at: number;
}

let lastHttpError: CapturedHttpError | null = null;

/** Quanto tempo depois da falha um aviso genérico ("Failed") ainda é atribuído a ela. */
const RECENT_MS = 5000;

/** Último erro HTTP, se aconteceu há pouco. Consumido uma vez: o próximo aviso não o repete. */
export function takeRecentHttpError(): CapturedHttpError | null {
	const error = lastHttpError;
	lastHttpError = null;
	return error && Date.now() - error.at <= RECENT_MS ? error : null;
}

/** Olha o último erro recente sem consumi-lo (para decidir se o aviso é de erro). */
export function peekRecentHttpError(): CapturedHttpError | null {
	return lastHttpError && Date.now() - lastHttpError.at <= RECENT_MS ? lastHttpError : null;
}

/**
 * Tira a mensagem legível do corpo de erro do backend.
 * Nest devolve `{ message }` (texto ou lista); alguns serviços devolvem texto puro.
 */
export function extractBackendMessage(error: HttpErrorResponse): string | null {
	const body = error.error;
	if (!body) return null;
	if (typeof body === 'string') return body.trim().slice(0, 500) || null;
	const message = body.message ?? body.error?.message ?? body.error;
	if (Array.isArray(message)) return message.join('\n');
	return typeof message === 'string' && message.trim() ? message.trim() : null;
}

/**
 * Guarda a última resposta de erro da API para o aviso de erro mostrar o motivo
 * real. As telas só abrem "Failed"; o motivo vinha no corpo da resposta e se perdia.
 * Não altera a requisição nem a resposta.
 */
/**
 * Requisições de bastidor (ex.: registro de telas abertas) marcam este token
 * para que uma falha delas não vire o "motivo" do próximo toast de outra tela.
 */
export const SKIP_ERROR_CAPTURE = new HttpContextToken<boolean>(() => false);

@Injectable()
export class HttpErrorCaptureInterceptor implements HttpInterceptor {
	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (request.context.get(SKIP_ERROR_CAPTURE)) return next.handle(request);
		return next.handle(request).pipe(
			tap({
				error: (error: unknown) => {
					if (!(error instanceof HttpErrorResponse)) return;
					lastHttpError = {
						message: extractBackendMessage(error),
						status: error.status,
						method: request.method,
						url: request.urlWithParams,
						at: Date.now(),
					};
				},
			}),
		);
	}
}
